'use client';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { palette } from '@peerless/ui-assets/palette';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { Animation, Link, useDisplaySearch } from '@peerless/ui-shared';
import { CompatibilityCheckResult } from './CompatibilityCheckResult';
import { SearchCompatibilityItem } from '@peerless/data-access/api';
import { ROUTE_FILTERS, usePathname, useSearchParams } from '@/navigation';
import { emitTelemetryEvent } from '@peerless/data-access-search-events';
interface Props {
  sku: string;
  defaultKeyword?: string;
  data?: SearchCompatibilityItem[];
}

export function CompatibilityChecker({ sku, defaultKeyword, data }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const {
    data: displayResults,
    isLoading,
    search: displaySearch,
    reset: resetDisplaySearch,
  } = useDisplaySearch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [compatibilityResult, setCompatibilityResult] = useState<SearchCompatibilityItem>();
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [searchStartTime, setSearchStartTime] = useState<number | null>(null);
  const searchParam = searchParams.get('display');

  const handleSetAnchroEl = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget.parentElement);
    },
    [setAnchorEl]
  );

  const checkCompatibility = useCallback(
    (keyword: string) => {
      const result = data?.find(item => item.text === keyword);
      setCompatibilityResult(result);

      emitTelemetryEvent(
        {
          searchType: 'compatibility',
          searchSource: 'dotnet-api',
          matchFound: !!result,
          resultCount: result ? 1 : 0,
          latencyMs: searchStartTime ? performance.now() - searchStartTime : 0,
          queryText: keyword,
          queryParams: { productSku: sku },
        },
        locale,
        'US'
      );
    },
    [data, sku, locale, searchStartTime]
  );

  const handleSelect = useCallback(
    (displayName: string) => {
      checkCompatibility(displayName);
      setSelectedDevice(displayName);
      resetDisplaySearch();
      setKeyword(displayName);
    },
    [resetDisplaySearch, checkCompatibility]
  );

  const handleSearch = useCallback(() => {
    setSearchStartTime(performance.now());
    displaySearch(keyword);
    setSelectedDevice('');
    setCompatibilityResult(undefined);
  }, [displaySearch, keyword]);

  const reset = useCallback(() => {
    setKeyword('');
    resetDisplaySearch();
    setSelectedDevice('');
    setCompatibilityResult(undefined);
    setSearchStartTime(null);
  }, [resetDisplaySearch]);

  const composeUrl = useCallback(
    (keyword: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('filter', ROUTE_FILTERS.COMPATIBILITY);
      params.set('display', keyword);
      return `${pathName}?${params.toString()}`;
    },
    [pathName, searchParams]
  );

  const displayNotFound =
    !isLoading && displayResults.keyword.length > 2 && displayResults.count === 0;

  useEffect(() => {
    if (defaultKeyword) {
      handleSelect(defaultKeyword);
    }
  }, [defaultKeyword, handleSelect]);

  return (
    <Box my={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <FontAwesomeIcon fixedWidth icon={icons.alertCircle} color={palette.primary.main} />
        <Typography m={0} variant="h5">
          {t('Product.header-check-display-compatibility')}
        </Typography>
      </Stack>
      <Stack direction="row" mt={2}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          sx={{ '& .MuiInputBase-root': { pr: 0, height: '100%' } }}
          onClick={handleSetAnchroEl}
          value={keyword}
          onChange={evt => {
            evt.preventDefault();
            resetDisplaySearch();
            setKeyword(evt.target.value);
          }}
          onKeyDown={evt => {
            if (evt.key === 'Enter') {
              handleSearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={evt => {
                    evt.preventDefault();
                    reset();
                  }}
                >
                  <FontAwesomeIcon fixedWidth icon={icons.close} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ width: '15%' }}
          onClick={evt => {
            evt.preventDefault();
            handleSearch();
          }}
        >
          <FontAwesomeIcon fixedWidth icon={icons.search} />
        </Button>
      </Stack>
      {isLoading && (
        <LinearProgress
          sx={{
            height: '4px',
            marginTop: '-4px',
            display: 'block',
            backgroundColor: palette.primary.light,
          }}
        />
      )}
      <Typography variant="subtitle1" textAlign="right">
        {t('Search.powered-by-mountfinder')}
      </Typography>
      <Popper
        anchorEl={anchorEl}
        open={displayResults.count > 0}
        placement="bottom"
        transition
        keepMounted
        sx={{ width: anchorEl?.offsetWidth, zIndex: 2 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 1],
            },
          },
          { name: 'flip', enabled: false },
        ]}
      >
        {({ TransitionProps }) => (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TransitionProps is always defined based on transition prop above
          <Animation {...TransitionProps!}>
            <Paper
              elevation={1}
              sx={{
                maxHeight: '50vh',
                overflow: 'auto',
              }}
            >
              <List dense>
                {displayResults.products.map(item => (
                  <ListItemButton
                    component={Link}
                    replace
                    key={item.title}
                    onClick={evt => {
                      //This is fallback for the case when url is the same and refresh is not happening
                      if (searchParam === item.title) {
                        evt.preventDefault();
                        handleSelect(item.title);
                      }
                    }}
                    href={composeUrl(item.title)}
                  >
                    <ListItemText>{item.title}</ListItemText>
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Animation>
        )}
      </Popper>
      <CompatibilityCheckResult
        keyword={keyword}
        data={compatibilityResult}
        displayNotFound={displayNotFound}
        deviceName={selectedDevice}
      />
    </Box>
  );
}
